import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { shopperHeaders } from '@/lib/shopper';

const CartContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_CART = { items: [], count: 0, total_amount: 0 };

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(EMPTY_CART);
  const [loading, setLoading] = useState(true);
  // درج السلة: يتفتح من أي مكان (الهيدر، المفضلة...)
  const [isCartOpen, setIsCartOpen] = useState(false);
  const { token, loading: authLoading } = useAuth();

  const request = useCallback(
    async (method, url, data) => {
      const response = await axios({ method, url: `${API}${url}`, data, headers: shopperHeaders(token) });
      setCart(response.data?.data || EMPTY_CART);
      return response.data;
    },
    [token]
  );

  const fetchCart = useCallback(async () => {
    try {
      await request('get', '/cart');
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  }, [request]);

  // يعاد التحميل عند تسجيل الدخول/الخروج (سلة الزائر بتتدمج في الحساب من الباك إند)
  useEffect(() => {
    if (!authLoading) fetchCart();
  }, [authLoading, fetchCart]);

  const addToCart = (variantId, quantity = 1) => request('post', '/cart', { variant_id: variantId, quantity });

  const updateQuantity = (itemId, quantity) => request('put', `/cart/items/${itemId}`, { quantity });

  const removeItem = (itemId) => request('delete', `/cart/items/${itemId}`);

  const clearCart = () => request('delete', '/cart');

  const openCart = useCallback(() => setIsCartOpen(true), []);
  const closeCart = useCallback(() => setIsCartOpen(false), []);

  /** العنصر اللي في السلة لنفس التركيبة (لو موجود). */
  const findCartItem = (variantId) => cart.items?.find((item) => item.variant_id === variantId) || null;

  return (
    <CartContext.Provider
      value={{
        cart,
        cartCount: cart.count || 0,
        loading,
        addToCart,
        updateQuantity,
        removeItem,
        clearCart,
        fetchCart,
        findCartItem,
        isCartOpen,
        openCart,
        closeCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

/** رسالة الخطأ اللي راجعة من الباك إند (زي "هذا المنتج غير متاح حالياً"). */
export const apiErrorMessage = (error, fallback) => error?.response?.data?.message || fallback;
